import * as React from 'react';
import { LuCheck, LuChevronLeft, LuChevronRight } from 'react-icons/lu';

import { Box } from './box';
import { HStack, VStack } from './stack';
import { Text } from './text';
import { Flex } from './flex';
import { Progress } from './progress';
import { Button } from './button';
import { useMultiStepForm } from './multi-step-form';

// Use breakpoint hook equivalent
const useBreakpointValue = (values: { base: boolean; md: boolean }) => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile ? values.base : values.md;
};

export interface StepNavigatorProps {
  steps: Array<{
    id: string;
    title: string;
    description?: string;
    isOptional?: boolean;
  }>;
  variant?: 'horizontal' | 'vertical' | 'minimal';
  showLabels?: boolean;
  showDescription?: boolean;
  showProgress?: boolean;
  className?: string;
}

/**
 * StepNavigator - Navigation component for multi-step forms
 *
 * Features:
 * - Visual progress indication
 * - Step labels and descriptions
 * - Click-to-navigate (for completed steps)
 * - Responsive design (horizontal/vertical layouts)
 * - Multiple visual variants
 *
 * @example
 * ```tsx
 * <StepNavigator
 *   steps={[
 *     { id: 'personal', title: 'Personal Info', description: 'Basic details' },
 *     { id: 'contact', title: 'Contact', description: 'Email and phone' },
 *     { id: 'review', title: 'Review', description: 'Confirm details' }
 *   ]}
 *   variant="horizontal"
 *   showLabels
 *   showDescription
 * />
 * ```
 */
export const StepNavigator: React.FC<StepNavigatorProps> = ({
  steps,
  variant = 'horizontal',
  showLabels = true,
  showDescription = false,
  showProgress = true,
  className,
}) => {
  const {
    currentStep,
    totalSteps,
    goToStep,
    canGoNext: _canGoNext,
    canGoPrevious: _canGoPrevious,
  } = useMultiStepForm();

  const isMobile = useBreakpointValue({ base: true, md: false });
  const effectiveVariant = isMobile ? 'minimal' : variant;

  // Calculate step states
  const getStepState = (stepIndex: number) => {
    if (stepIndex < currentStep) return 'completed';
    if (stepIndex === currentStep) return 'active';
    return 'pending';
  };

  const getStepColor = (state: string) => {
    switch (state) {
      case 'completed':
        return 'green.500';
      case 'active':
        return 'blue.500';
      default:
        return 'gray.300';
    }
  };

  const canNavigateToStep = (stepIndex: number) => {
    // Can navigate to current step or any completed step
    return stepIndex <= currentStep;
  };

  const handleStepClick = (stepIndex: number) => {
    if (canNavigateToStep(stepIndex)) {
      goToStep(stepIndex);
    }
  };

  // Reusable circle wrapper since Chakra's Circle causes an object-render error in some versions
  const CircleWrapper: React.FC<{
    size: string;
    children?: React.ReactNode;
    bg?: string;
    color?: string;
    fontSize?: string;
    fontWeight?: string;
    border?: string;
    borderColor?: string;
    shadow?: string;
  }> = ({ size, children, bg, color, fontSize, fontWeight, border, borderColor, shadow }) => (
    <Flex
      align="center"
      justify="center"
      borderRadius="full"
      width={size}
      height={size}
      backgroundColor={bg}
      color={color}
      fontSize={fontSize}
      fontWeight={fontWeight}
      border={border}
      borderColor={borderColor}
      shadow={shadow}
    >
      {children}
    </Flex>
  );

  if (effectiveVariant === 'minimal') {
    return (
      <Box className={className}>
        <VStack gap={4}>
          {/* Progress bar */}
          {showProgress && (
            <Box width="full">
              <Flex justify="between" marginBottom={2}>
                <Text fontSize="sm" fontWeight="medium">
                  Step {currentStep + 1} of {totalSteps}
                </Text>
                <Text fontSize="sm" color="#4B5563">
                  {Math.round(((currentStep + 1) / totalSteps) * 100)}%
                </Text>
              </Flex>
              <Progress.Root value={((currentStep + 1) / totalSteps) * 100} size="sm">
                <Progress.Track backgroundColor="#F3F4F6" borderRadius="md">
                  <Progress.Range backgroundColor="#0066CC" />
                </Progress.Track>
              </Progress.Root>
            </Box>
          )}

          {/* Current step info */}
          <Box textAlign="center">
            <Text fontWeight="bold" fontSize="lg">
              {steps[currentStep]?.title}
            </Text>
            {showDescription && steps[currentStep]?.description && (
              <Text fontSize="sm" color="#4B5563" marginTop={1}>
                {steps[currentStep].description}
              </Text>
            )}
          </Box>
        </VStack>
      </Box>
    );
  }

  if (effectiveVariant === 'vertical') {
    return (
      <Box className={className}>
        <VStack gap={4} align="stretch">
          {steps.map((step, index) => {
            const state = getStepState(index);
            const isClickable = canNavigateToStep(index);

            return (
              <HStack
                key={step.id}
                gap={4}
                cursor={isClickable ? 'pointer' : 'default'}
                onClick={() => handleStepClick(index)}
                padding={3}
                borderRadius="md"
                backgroundColor={state === 'active' ? 'blue.50' : 'transparent'}
                borderLeft="3px solid"
                borderColor={getStepColor(state)}
                transition="all 0.2s"
                _hover={isClickable ? { bg: 'gray.50' } : {}}
              >
                {/* Step indicator */}
                <CircleWrapper
                  size="8"
                  bg={getStepColor(state)}
                  color="white"
                  fontSize="sm"
                  fontWeight="bold"
                >
                  {state === 'completed' ? <LuCheck size={16} /> : index + 1}
                </CircleWrapper>

                {/* Step content */}
                <VStack align="flex-start" gap={0} flex={1}>
                  <Text
                    fontWeight={state === 'active' ? 'bold' : 'medium'}
                    color={state === 'pending' ? 'gray.500' : 'inherit'}
                  >
                    {step.title}
                    {step.isOptional && (
                      <Text as="span" fontSize="xs" color="#6B7280" marginLeft={2}>
                        (Optional)
                      </Text>
                    )}
                  </Text>
                  {showDescription && step.description && (
                    <Text fontSize="sm" color={state === 'pending' ? 'gray.400' : 'gray.600'}>
                      {step.description}
                    </Text>
                  )}
                </VStack>
              </HStack>
            );
          })}
        </VStack>
      </Box>
    );
  }

  // Horizontal variant (default)
  return (
    <Box className={className}>
      <VStack gap={6}>
        {/* Step indicators */}
        <HStack gap={0} width="full" justify="center">
          {steps.map((step, index) => {
            const state = getStepState(index);
            const isClickable = canNavigateToStep(index);
            const isLast = index === steps.length - 1;

            return (
              <React.Fragment key={step.id}>
                {/* Step circle and content */}
                <VStack
                  gap={2}
                  cursor={isClickable ? 'pointer' : 'default'}
                  onClick={() => handleStepClick(index)}
                  padding={2}
                  borderRadius="md"
                  transition="all 0.2s"
                  _hover={isClickable ? { bg: 'gray.50' } : {}}
                  minWidth="120px"
                >
                  {/* Step circle */}
                  <CircleWrapper
                    size="10"
                    bg={getStepColor(state)}
                    color="white"
                    fontSize="sm"
                    fontWeight="bold"
                    border={state === 'active' ? '3px solid' : 'none'}
                    borderColor="#99C2FF"
                    shadow={state === 'active' ? 'md' : 'sm'}
                  >
                    {state === 'completed' ? <LuCheck size={20} /> : index + 1}
                  </CircleWrapper>

                  {/* Step label */}
                  {showLabels && (
                    <VStack gap={0} textAlign="center">
                      <Text
                        fontSize="sm"
                        fontWeight={state === 'active' ? 'bold' : 'medium'}
                        color={state === 'pending' ? 'gray.500' : 'inherit'}
                      >
                        {step.title}
                        {step.isOptional && (
                          <Text as="span" fontSize="xs" color="#6B7280" display="block">
                            (Optional)
                          </Text>
                        )}
                      </Text>
                      {showDescription && step.description && (
                        <Text
                          fontSize="xs"
                          color={state === 'pending' ? 'gray.400' : 'gray.600'}
                          lineClamp={2}
                        >
                          {step.description}
                        </Text>
                      )}
                    </VStack>
                  )}
                </VStack>

                {/* Connector line */}
                {!isLast && (
                  <Box
                    flex={1}
                    height="2px"
                    backgroundColor={index < currentStep ? 'green.500' : 'gray.200'}
                    marginX={2}
                    transition="all 0.3s"
                  />
                )}
              </React.Fragment>
            );
          })}
        </HStack>

        {/* Progress bar (alternative visualization) */}
        {showProgress && (
          <Box width="full" maxWidth="md">
            <Progress.Root value={((currentStep + 1) / totalSteps) * 100} size="sm">
              <Progress.Track backgroundColor="#F3F4F6" borderRadius="full">
                <Progress.Range backgroundColor="#0066CC" />
              </Progress.Track>
            </Progress.Root>
            <HStack justify="space-between" marginTop={2}>
              <Text fontSize="xs" color="#4B5563">
                {Math.round(((currentStep + 1) / totalSteps) * 100)}% Complete
              </Text>
              <Text fontSize="xs" color="#4B5563">
                {currentStep + 1} of {totalSteps}
              </Text>
            </HStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

/**
 * StepNavigationButtons - Standard navigation buttons for multi-step forms
 *
 * Features:
 * - Previous/Next buttons with proper states
 * - Submit button on last step
 * - Loading states
 * - Customizable button text
 *
 * @example
 * ```tsx
 * <StepNavigationButtons
 *   previousText="Back"
 *   nextText="Continue"
 *   submitText="Complete Order"
 *   isSubmitting={mutation.isPending}
 * />
 * ```
 */
export interface StepNavigationButtonsProps {
  previousText?: string;
  nextText?: string;
  submitText?: string;
  isSubmitting?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  onSubmit?: () => void;
  showPrevious?: boolean;
  showNext?: boolean;
  className?: string;
}

export const StepNavigationButtons: React.FC<StepNavigationButtonsProps> = ({
  previousText = 'Previous',
  nextText = 'Next',
  submitText = 'Submit',
  isSubmitting = false,
  onPrevious,
  onNext,
  onSubmit,
  showPrevious = true,
  showNext = true,
  className,
}) => {
  const { isFirstStep, isLastStep, canGoNext, canGoPrevious, goNext, goPrevious, submitForm } =
    useMultiStepForm();

  const handlePrevious = () => {
    if (onPrevious) {
      onPrevious();
    } else {
      goPrevious();
    }
  };

  const handleNext = () => {
    if (onNext) {
      onNext();
    } else {
      goNext();
    }
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit();
    } else {
      submitForm();
    }
  };

  return (
    <HStack gap={4} justify="space-between" width="full" className={className}>
      {/* Previous Button */}
      <Box>
        {showPrevious && !isFirstStep && (
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={!canGoPrevious || isSubmitting}
            leftIcon={<LuChevronLeft />}
          >
            {previousText}
          </Button>
        )}
      </Box>

      {/* Next/Submit Button */}
      <Box>
        {isLastStep ? (
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            disabled={!canGoNext}
          >
            {submitText}
          </Button>
        ) : (
          showNext && (
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={!canGoNext || isSubmitting}
              rightIcon={<LuChevronRight />}
            >
              {nextText}
            </Button>
          )
        )}
      </Box>
    </HStack>
  );
};
